import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';
import type { GranulationTypeId } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import didInsert from 'webapp/modifiers/did-insert';
import BarLoaderComponent from 'webapp/components/bar-loader';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import FormCompareDropdownComponent from 'webapp/components/form/compare/dropdown';
import FormCumulativeSwitchComponent from 'webapp/components/form/cumulative-switch';
import FormGranulationDropdownComponent from 'webapp/components/form/granulation/dropdown';
import FormRangeDropdownComponent from 'webapp/components/form/range/dropdown';
import type { Args } from 'webapp/components/popup/inner/context';
import PopupInnerContextComponent from 'webapp/components/popup/inner/context';
import SectionWithTogglerComponent from 'webapp/components/section-with-toggler';

import type DateRange from 'webapp/models-custom/date-range';
import { CompareCategory } from 'webapp/models-custom/ds-compare';
import type DsCompare from 'webapp/models-custom/ds-compare';
import type MetricLimitations from 'webapp/models-custom/metric-limitations';

export default class BlockPopupPreviewPropsComponent extends PopupInnerContextComponent<PopupData.Block.PreviewProps> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	@tracked limitationsCopy: MetricLimitations;
	@tracked isLoading = true;
	@tracked isSectionWithTogglerOpen = false;
	@tracked isCheckboxSelected = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.limitationsCopy = this.metricLimitations.createCopy();

		this.metricLimitations.updateLimitations();
		void this.metricLimitations.limitationsLoaded?.then(() => {
			this.isLoading = false;
		});
	}

	readonly compareExceptionIds: string[] = [
		String(CompareCategory.GOAL),
		String(CompareCategory.OTHER_METRIC),
	];

	get metricLimitations() {
		return this.popupData.metricLimitations;
	}

	get dateRange() {
		return this.metricLimitations?.dateRange;
	}

	get exceptionIds() {
		return this.popupData.exceptionIds;
	}

	get granulationExceptionIds() {
		return this.popupData.granulationExceptionIds;
	}

	get showAllTime() {
		return this.popupData.showAllTime;
	}

	get hideRangeSelectDropdown() {
		return this.popupData.hideRangeSelectDropdown;
	}

	get hideUnsupported() {
		return !this.currentUserService.isLoggedIn;
	}

	get hideCumulativeSwitch() {
		// don't show empty switch when disabled
		return (
			this.hideUnsupported &&
			this.limitationsCopy?.limitationForCumulative?.onlyOneOptionAvailable &&
			!this.limitationsCopy?.dateRange?.granulationCumulative
		);
	}

	get isDarkTheme() {
		return this.args.model.theme === 'dark';
	}

	get themeForSwitch() {
		return this.popupData.switchTheme ?? this.args.model.theme;
	}

	get themeForButton() {
		const prefix = 'theme-';
		if (this.isDarkTheme) {
			return `${prefix}black`;
		} else if (this.popupData.buttonTheme) {
			return `${prefix}${this.popupData.buttonTheme}`;
		} else {
			return '';
		}
	}

	get skipGranulationCollapsing() {
		return !this.popupData.collapseGranulation;
	}

	get isDataRangeDisabled() {
		return this.limitationsCopy.dateRange?.isEmpty;
	}

	get compareAsDsCompare(): DsCompare | undefined {
		const compare = this.limitationsCopy.compare;
		return compare!;
	}

	get trackingOrigin(): string | undefined {
		return this.popupData.trackingOrigin;
	}

	get showMasterCompare() {
		return !!this.popupData.showMasterCompare;
	}

	@action
	changeDateRange(dateRange: DateRange) {
		const limitationsCopy = this.limitationsCopy;
		if (!limitationsCopy) {
			return;
		}

		limitationsCopy.updateDateRangeFromOnlyRangeFormula(dateRange.rangeFormula);
		limitationsCopy.selectOptimalValidGranulation();
		limitationsCopy.compare?.updateFromDateRange(dateRange);
	}

	@action
	changeGranulation(granulationId: AnySimpleValue) {
		const limitationsCopy = this.limitationsCopy;
		if (!limitationsCopy) {
			return;
		}

		limitationsCopy.dateRange?.updateGranulationFromTypeId(granulationId as GranulationTypeId);
	}

	@action
	changeCumulative(cumulative: boolean) {
		const limitationsCopy = this.limitationsCopy;
		if (!limitationsCopy) {
			return;
		}

		if (limitationsCopy.dateRange) {
			limitationsCopy.dateRange.granulationCumulative = cumulative;
		}
	}

	@action
	changeCompare(newCompare: DsCompare): void {
		const limitationsCopy = this.limitationsCopy;
		if (!limitationsCopy) {
			return;
		}

		limitationsCopy.compare?.updateFromFormula(
			newCompare.getFormula(),
			limitationsCopy.dateRange!,
		);

		limitationsCopy.selectOptimalValidGranulation();
	}

	@action
	apply() {
		const changedDateRange = this.limitationsCopy.dateRange!,
			currentFormula = this.metricLimitations.dateRange?.formula;
		const changedCompare = this.limitationsCopy.compare!;

		if (
			this.popupData.trackingOrigin &&
			(!currentFormula || !changedDateRange.isGranulationEqual(currentFormula))
		) {
			const metricData = this.metricLimitations.metric;
			if (metricData?.key) {
				const metric = this.mmMetricsService.getMetricForKeyAndSpaceAccessId(
					metricData.key,
					metricData.spaceAccessId,
				);
				this.mmMetricsService.trackMetricDataEvent(
					TrackingEvent.GRANULATION_CHANGED,
					metric,
					changedDateRange,
					this.popupData.trackingOrigin,
				);
			}
		}

		this.popupData.onChange(changedDateRange, changedCompare, this.isCheckboxSelected);
		this.close();
	}

	@action
	onTogglerChange(): void {
		this.isSectionWithTogglerOpen = !this.isSectionWithTogglerOpen;
		this.scheduleReposition();
	}

	@action
	onFooterCheckboxChange(): void {
		this.isCheckboxSelected = !this.isCheckboxSelected;
	}
	<template>
		{{#if this.isLoading}}
			<div class="loader-ct">
				<BarLoaderComponent />
			</div>
		{{else if this.limitationsCopy}}
			<div class="ct" {{didInsert this.reposition}}>

				{{#unless this.hideRangeSelectDropdown}}
					<div class="row">
						<FormRangeDropdownComponent
							@metricLimitations={{this.limitationsCopy}}
							@onChange={{this.changeDateRange}}
							@placeholder="Date range"
							@theme={{@model.theme}}
							@exceptionIds={{this.exceptionIds}}
							@trackingOrigin={{this.trackingOrigin}}
						/>
					</div>
				{{/unless}}

				<SectionWithTogglerComponent
					@isOpen={{this.isSectionWithTogglerOpen}}
					@title={{if this.isSectionWithTogglerOpen "Less options" "More options"}}
					@toggle={{this.onTogglerChange}}
					@skip={{this.skipGranulationCollapsing}}
				>

					<div class="row row--align-vertical">
						<div class="left">
							<FormGranulationDropdownComponent
								@metricLimitations={{this.limitationsCopy}}
								@onChange={{this.changeGranulation}}
								@placeholder="Graph by"
								@theme={{@model.theme}}
								@exceptionIds={{this.granulationExceptionIds}}
								@trackingOrigin={{this.trackingOrigin}}
							/>
						</div>

						{{#unless this.hideCumulativeSwitch}}
							<div class="right">
								<FormCumulativeSwitchComponent
									@metricLimitations={{this.limitationsCopy}}
									@onChange={{this.changeCumulative}}
									@theme={{this.themeForSwitch}}
								/>
							</div>
						{{/unless}}
					</div>

					{{#if this.showMasterCompare}}
						<div class="row">
							<FormCompareDropdownComponent
								@metricLimitations={{this.limitationsCopy}}
								@compare={{this.compareAsDsCompare}}
								@onChange={{this.changeCompare}}
								@placeholder="Compare with"
								@theme={{@model.theme}}
								@trackingOrigin={{this.trackingOrigin}}
								@exceptionIds={{this.compareExceptionIds}}
							/>
						</div>
					{{/if}}

				</SectionWithTogglerComponent>

				<div class="footer">
					<a
						role="button"
						class="button save
							{{this.themeForButton}}
							{{if this.isDataRangeDisabled 'disabled'}}"
						{{on "click" this.apply}}
					>Apply
					</a>

					{{#if this.popupData.footerCheckboxLabel}}
						<FormCheckboxComponent
							@name="footer-checkbox"
							@selected={{this.isCheckboxSelected}}
							@label={{this.popupData.footerCheckboxLabel}}
							@onChange={{this.onFooterCheckboxChange}}
							@theme={{this.popupData.buttonTheme}}
						/>
					{{/if}}
				</div>
			</div>

		{{/if}}
	</template>
}
